import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap } from 'rxjs';

import { AjsStateService } from '../../ajs-state.service';
import { MetadataAdapter } from '../../core/adapters/metadata.adapter';
import { MetadataRequester } from '../../requesters/core/metadata.requester';
import {
	fetchMetadataListFail,
	fetchMetadataListSuccess,
	initMetadataList,
	navigateToMetadataSource,
	navigateToMetadataSourceFail,
	navigateToMetdataSourceSuccess,
	refreshMetadataList,
} from './metadata.actions';

@Injectable()
export class MetadataEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToMetadataSource = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToMetadataSource),
				switchMap((action) =>
					this.metadataRequester
						.getElementIdFromMetadata(action.metadata.id, action.metadata.type)
						.pipe(
							map((response) => ({
								response,
								action,
							}))
						)
				),
				tap(({ response, action }) => {
					const sourceUrl = this.metadataAdapter.parseSourcePageUrl({
						id: response.v2 ?? -1,
						type: action.metadata.type,
						subType: '',
					});
					window.open(sourceUrl, '_blank');
					this.store.dispatch(navigateToMetdataSourceSuccess());
				}),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					this.store.dispatch(
						navigateToMetadataSourceFail({
							error,
						})
					);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);
	// ##########################################################
	// Business Effects
	// ##########################################################

	public initMetadataList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initMetadataList),
			switchMap(() => this.metadataRequester.getMetadataList(true)),
			map((response) =>
				fetchMetadataListSuccess({
					metadataList:
						this.metadataAdapter.generateMetadataListEntity(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMetadataListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshMetadataList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshMetadataList),
			switchMap(() => this.metadataRequester.getMetadataList(false)),
			map((response) =>
				fetchMetadataListSuccess({
					metadataList:
						this.metadataAdapter.generateMetadataListEntity(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMetadataListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly metadataRequester: MetadataRequester,
		private readonly metadataAdapter: MetadataAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store
	) {}
}
